import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Chip,
    Paper,
    Grid,
    Divider,
    CircularProgress,
    Alert,
    Stack,
    Card,
    CardContent,
    TextField,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
    Schedule as PendingIcon,
    CheckCircle as CheckIcon,
    SwapHoriz as ReassignIcon,
    Description as DescriptionIcon,
    Assignment as AssignmentIcon,
    Science as ScienceIcon,
    QuestionAnswer as QueryIcon,
    Send as SendIcon
} from '@mui/icons-material';
import {
    PageContainer,
    HeaderContainer,
    backButtonStyles
} from '@Pages/RnDMember/ExitRequestResponseUnified/ExitRequestResponseStyles';
import PageTitle from '@Components/PageTitle/PageTitle';
import axiosInstance from '../../../API/axios';
import { format } from 'date-fns';

const STATUS_COLORS = {
    PENDING: 'warning',
    RESPONDED: 'success',
    REASSIGNED: 'info',
    COMPLETED: 'default',
    CANCELLED: 'error'
};

const STATUS_ICONS = {
    PENDING: <PendingIcon fontSize="small" />,
    RESPONDED: <CheckIcon fontSize="small" />,
    REASSIGNED: <ReassignIcon fontSize="small" />
};

// Helper: info row component
const InfoRow = ({ label, value, fullWidth = false }) => (
    <Grid item xs={12} md={fullWidth ? 12 : 6}>
        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
            {label}
        </Typography>
        {typeof value === 'string' || typeof value === 'number' ? (
            <Typography variant="body1">{value || '-'}</Typography>
        ) : (
            value || <Typography variant="body1">-</Typography>
        )}
    </Grid>
);

// Helper: section card component
const SectionCard = ({ icon, title, children, sx = {} }) => (
    <Card sx={{ mb: 3, ...sx }} variant="outlined">
        <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                {icon}
                <Typography variant="h6" fontWeight="bold">
                    {title}
                </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {children}
        </CardContent>
    </Card>
);

const SupportRequestDetail = () => {
    const { requestId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [request, setRequest] = useState(null);
    const [exitPlan, setExitPlan] = useState(null);
    const [exitRequest, setExitRequest] = useState(null);
    const [rdResponse, setRdResponse] = useState(null);
    const [error, setError] = useState(null);
    const [response, setResponse] = useState('');
    const [submitError, setSubmitError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    const fetchRequestDetails = useCallback(async () => {
        try {
            setLoading(true);
            const requestResponse = await axiosInstance.get(`/support-requests/${requestId}`);
            setRequest(requestResponse.data);

            // Fetch the full exit plan details
            if (requestResponse.data.exitPlanId) {
                const exitPlanResponse = await axiosInstance.get(`/exit-plans/${requestResponse.data.exitPlanId}`);
                setExitPlan(exitPlanResponse.data);

                // Fetch exit request details if available
                if (exitPlanResponse.data.exitRequestId) {
                    try {
                        const exitRequestResponse = await axiosInstance.get(`/exit-requests/${exitPlanResponse.data.exitRequestId}`);
                        setExitRequest(exitRequestResponse.data);
                    } catch (err) {
                        console.warn('Failed to fetch exit request:', err);
                    }
                }

                // Fetch RD response details if available
                if (exitPlanResponse.data.responseId) {
                    try {
                        const rdResponseData = await axiosInstance.get(`/exit-request-responses/${exitPlanResponse.data.responseId}`);
                        setRdResponse(rdResponseData.data);
                    } catch (err) {
                        console.warn('Failed to fetch RD response:', err);
                    }
                }
            }

            setError(null);
        } catch (err) {
            console.error('Error fetching support request details:', err);
            setError('Failed to load support request details');
        } finally {
            setLoading(false);
        }
    }, [requestId]);

    useEffect(() => {
        fetchRequestDetails();
    }, [fetchRequestDetails]);

    const handleSubmitResponse = async () => {
        if (!response.trim()) {
            setSubmitError('Please enter a response');
            return;
        }

        try {
            setSubmitting(true);
            setSubmitError(null);

            await axiosInstance.post(`/support-requests/${requestId}/respond`, {
                supportResponse: response.trim()
            });

            await fetchRequestDetails();
            setResponse('');
        } catch (err) {
            console.error('Error submitting response:', err);
            setSubmitError(err.response?.data?.message || 'Failed to submit response');
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return '-';
        try {
            let date;
            if (Array.isArray(dateValue)) {
                const [year, month, day, hour = 0, minute = 0, second = 0] = dateValue;
                date = new Date(year, month - 1, day, hour, minute, second);
            } else if (typeof dateValue === 'string') {
                date = new Date(dateValue);
            } else {
                return String(dateValue);
            }
            if (isNaN(date.getTime())) return String(dateValue);
            return format(date, 'MMM dd, yyyy HH:mm');
        } catch {
            return String(dateValue);
        }
    };

    const formatDateOnly = (dateValue) => {
        if (!dateValue) return '-';
        try {
            let date;
            if (Array.isArray(dateValue)) {
                const [year, month, day] = dateValue;
                date = new Date(year, month - 1, day);
            } else if (typeof dateValue === 'string') {
                date = new Date(dateValue);
            } else {
                return String(dateValue);
            }
            if (isNaN(date.getTime())) return String(dateValue);
            return format(date, 'MMM dd, yyyy');
        } catch {
            return String(dateValue);
        }
    };

    if (loading) {
        return (
            <PageContainer>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </PageContainer>
        );
    }

    if (error) {
        return (
            <PageContainer>
                <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{ mt: 2 }}
                >
                    Go Back
                </Button>
            </PageContainer>
        );
    }

    if (!request) {
        return (
            <PageContainer>
                <Alert severity="warning" sx={{ mt: 2 }}>Support request not found</Alert>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{ mt: 2 }}
                >
                    Go Back
                </Button>
            </PageContainer>
        );
    }

    const canRespond = request.status === 'PENDING' || request.status === 'REASSIGNED';
    const ingredient = rdResponse?.ingredient || exitRequest?.ingredient || {
        name: exitPlan?.ingredientName || request.ingredientName,
        casNumber: exitPlan?.casNumber
    };
    const products = rdResponse?.products || [];

    return (
        <PageContainer>
            {/* Header */}
            <HeaderContainer>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={backButtonStyles}
                >
                    Back
                </Button>
            </HeaderContainer>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <PageTitle title={`Support Request #${request.id}`} />
                <Chip
                    icon={STATUS_ICONS[request.status]}
                    label={request.status}
                    color={STATUS_COLORS[request.status] || 'default'}
                    sx={{ fontWeight: 'bold', fontSize: '0.875rem', height: 32 }}
                />
            </Box>

            {/* Request Overview Banner */}
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 2.5, 
                    mb: 3, 
                    bgcolor: 'primary.50', 
                    border: '1px solid',
                    borderColor: 'primary.200',
                    borderRadius: 2
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="textSecondary">Ingredient</Typography>
                        <Typography variant="body1" fontWeight="bold">
                            {ingredient?.name || request.ingredientName || 'N/A'}
                        </Typography>
                        {ingredient?.casNumber && (
                            <Typography variant="caption" color="textSecondary">CAS: {ingredient.casNumber}</Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="textSecondary">Requested By</Typography>
                        <Typography variant="body1" fontWeight="bold">
                            {request.createdBy?.username || '-'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {request.createdBy?.email}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="textSecondary">Created</Typography>
                        <Typography variant="body1" fontWeight="bold">
                            {formatDate(request.createdAt)}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="textSecondary">Assigned To</Typography>
                        <Typography variant="body1" fontWeight="bold">
                            {request.providerUsername}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">{request.providerEmail}</Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* Navigation Tabs */}
            <Paper sx={{ mb: 3 }} variant="outlined">
                <Tabs 
                    value={activeTab} 
                    onChange={(e, v) => setActiveTab(v)}
                    variant="fullWidth"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab icon={<QueryIcon />} label="Support Query & Response" iconPosition="start" />
                    <Tab icon={<DescriptionIcon />} label="Exit Request Info" iconPosition="start" />
                    <Tab icon={<ScienceIcon />} label="R&D Impact Assessment" iconPosition="start" />
                    <Tab icon={<AssignmentIcon />} label="Exit Plan" iconPosition="start" />
                </Tabs>
            </Paper>

            {/* Tab 0: Support Query & Response */}
            {activeTab === 0 && (
                <>
                    {/* Support Query */}
                    <SectionCard icon={<QueryIcon color="primary" />} title="Support Query">
                        <Paper sx={{ p: 2.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                                {request.supportQuery}
                            </Typography>
                        </Paper>
                    </SectionCard>

                    {/* Previous Response */}
                    {request.supportResponse && (
                        <SectionCard icon={<CheckIcon color="success" />} title="Support Response">
                            <Paper sx={{ p: 2.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 2 }}>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                                    {request.supportResponse}
                                </Typography>
                            </Paper>
                            {(request.respondedAt || request.responseDate) && (
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                    Responded on {formatDate(request.respondedAt || request.responseDate)}
                                </Typography>
                            )}
                        </SectionCard>
                    )}

                    {/* Response Form */}
                    {canRespond && (
                        <SectionCard icon={<SendIcon color="primary" />} title="Submit Your Response">
                            {submitError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {submitError}
                                </Alert>
                            )}
                            <TextField
                                label="Your Response"
                                value={response}
                                onChange={(e) => setResponse(e.target.value)}
                                multiline
                                rows={8}
                                fullWidth
                                placeholder="Provide your support response here. Include any recommendations, clarifications, or action items..."
                                required
                                error={!!submitError && !response.trim()}
                                sx={{ mb: 2 }}
                            />
                            <Box display="flex" justifyContent="flex-end" gap={2}>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate(-1)}
                                    disabled={submitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleSubmitResponse}
                                    disabled={submitting || !response.trim()}
                                    startIcon={submitting ? <CircularProgress size={18} /> : <SendIcon />}
                                >
                                    {submitting ? 'Submitting...' : 'Submit Response'}
                                </Button>
                            </Box>
                        </SectionCard>
                    )}
                </>
            )}

            {/* Tab 1: Exit Request Info */}
            {activeTab === 1 && (
                <SectionCard icon={<DescriptionIcon color="primary" />} title="Exit Request Information">
                    {exitRequest ? (
                        <Grid container spacing={2}>
                            <InfoRow label="Request ID" value={`#${exitRequest.id || request.exitRequestId || '-'}`} />
                            <InfoRow label="Status" value={
                                exitRequest.status ? (
                                    <Chip label={exitRequest.status} color="primary" size="small" />
                                ) : '-'
                            } />
                            <InfoRow label="Ingredient" value={
                                <Box>
                                    <Typography variant="body1" fontWeight="medium">
                                        {ingredient?.name || exitRequest.ingredientName || 'N/A'}
                                    </Typography>
                                    {ingredient?.casNumber && (
                                        <Typography variant="caption" color="textSecondary">
                                            CAS Number: {ingredient.casNumber}
                                        </Typography>
                                    )}
                                </Box>
                            } />
                            {exitRequest.category && (
                                <InfoRow label="Category" value={exitRequest.category?.name || exitRequest.category} />
                            )}
                            {exitRequest.regulatoryDeadline && (
                                <InfoRow label="Regulatory Deadline" value={formatDateOnly(exitRequest.regulatoryDeadline)} />
                            )}
                            {exitRequest.description && (
                                <InfoRow label="Description" value={
                                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {exitRequest.description}
                                        </Typography>
                                    </Paper>
                                } fullWidth />
                            )}
                            {exitRequest.reason && (
                                <InfoRow label="Reason for Exit" value={
                                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {exitRequest.reason}
                                        </Typography>
                                    </Paper>
                                } fullWidth />
                            )}
                            {exitRequest.createdAt && (
                                <InfoRow label="Created" value={formatDate(exitRequest.createdAt)} />
                            )}
                            {exitRequest.createdBy && (
                                <InfoRow label="Created By" value={
                                    `${exitRequest.createdBy?.username || ''} (${exitRequest.createdBy?.email || ''})`
                                } />
                            )}
                        </Grid>
                    ) : (
                        <Alert severity="info">
                            Exit request information is not available for this support request.
                        </Alert>
                    )}
                </SectionCard>
            )}

            {/* Tab 2: R&D Response */}
            {activeTab === 2 && (
                <SectionCard icon={<ScienceIcon color="primary" />} title="R&D Impact Assessment">
                    {rdResponse ? (
                        <Grid container spacing={3}>
                            <InfoRow label="Impact Assessment ID" value={`#${rdResponse.id || '-'}`} />
                            <InfoRow label="Status" value={
                                rdResponse.status ? (
                                    <Chip label={rdResponse.status} color="info" size="small" />
                                ) : '-'
                            } />
                            
                            {/* Ingredient details from RD response */}
                            {rdResponse.ingredient && (
                                <>
                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 1 }}>
                                            Ingredient Assessment
                                        </Typography>
                                    </Grid>
                                    <InfoRow label="Ingredient" value={rdResponse.ingredient.name} />
                                    {rdResponse.ingredient.casNumber && (
                                        <InfoRow label="CAS Number" value={rdResponse.ingredient.casNumber} />
                                    )}
                                    {rdResponse.ingredient.function && (
                                        <InfoRow label="Function" value={rdResponse.ingredient.function} />
                                    )}
                                </>
                            )}

                            {/* Affected Products */}
                            {products && products.length > 0 && (
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 1 }}>
                                        Products Assessment
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: 'grey.100' }}>
                                                    <TableCell><strong>Product Name</strong></TableCell>
                                                    <TableCell><strong>Affected</strong></TableCell>
                                                    <TableCell><strong>Description</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {products.map((product, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{product.name || product.productName || '-'}</TableCell>
                                                        <TableCell>
                                                            <Chip 
                                                                label={product.isAffected ? 'Yes' : 'No'}
                                                                color={product.isAffected ? 'error' : 'success'}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                        <TableCell>{product.description || '-'}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            )}

                            {/* RD comments */}
                            {rdResponse.comments && (
                                <InfoRow label="R&D Comments" value={
                                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {rdResponse.comments}
                                        </Typography>
                                    </Paper>
                                } fullWidth />
                            )}

                            {rdResponse.additionalNotes && (
                                <InfoRow label="Additional Notes" value={
                                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {rdResponse.additionalNotes}
                                        </Typography>
                                    </Paper>
                                } fullWidth />
                            )}
                        </Grid>
                    ) : (
                        <Alert severity="info">
                            R&D response information is not available for this support request.
                        </Alert>
                    )}
                </SectionCard>
            )}

            {/* Tab 3: Exit Plan */}
            {activeTab === 3 && (
                <SectionCard icon={<AssignmentIcon color="primary" />} title="Exit Plan Details">
                    {exitPlan ? (
                        <Grid container spacing={3}>
                            <InfoRow label="Plan ID" value={`#${exitPlan.id || '-'}`} />
                            <InfoRow label="Plan Status" value={
                                exitPlan.status ? (
                                    <Chip label={exitPlan.status} color="primary" size="small" />
                                ) : '-'
                            } />
                            <InfoRow label="Exit Date" value={formatDateOnly(exitPlan.exitDate)} />

                            {exitPlan.endOfProductionDate && (
                                <InfoRow label="End of Production Date" value={formatDateOnly(exitPlan.endOfProductionDate)} />
                            )}
                            {exitPlan.endOfImportationDate && (
                                <InfoRow label="End of Importation Date" value={formatDateOnly(exitPlan.endOfImportationDate)} />
                            )}

                            {exitPlan.replacementPlan && (
                                <InfoRow label="Replacement Plan" value={
                                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {exitPlan.replacementPlan}
                                        </Typography>
                                    </Paper>
                                } fullWidth />
                            )}

                            {exitPlan.liquidationPlan && (
                                <InfoRow label="Liquidation Plan" value={
                                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {exitPlan.liquidationPlan}
                                        </Typography>
                                    </Paper>
                                } fullWidth />
                            )}

                            {exitPlan.additionalComments && (
                                <InfoRow label="Additional Comments" value={
                                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {exitPlan.additionalComments}
                                        </Typography>
                                    </Paper>
                                } fullWidth />
                            )}

                            {/* Affected Products from Plan */}
                            {products && products.filter(p => p.isAffected).length > 0 && (
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 1 }}>
                                        Affected Products
                                    </Typography>
                                    <Stack spacing={1}>
                                        {products
                                            .filter(p => p.isAffected)
                                            .map((product, index) => (
                                                <Paper key={index} sx={{ p: 2, bgcolor: 'grey.50' }} variant="outlined">
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {product.name || product.productName}
                                                    </Typography>
                                                    {product.description && (
                                                        <Typography variant="body2" color="textSecondary">
                                                            {product.description}
                                                        </Typography>
                                                    )}
                                                </Paper>
                                            ))}
                                    </Stack>
                                </Grid>
                            )}
                        </Grid>
                    ) : (
                        <Alert severity="info">
                            Exit plan information is not available for this support request.
                        </Alert>
                    )}
                </SectionCard>
            )}
        </PageContainer>
    );
};

export default SupportRequestDetail;
